import React, { useState, useEffect, useRef } from 'react';
import { userAPI, appointmentAPI, workHoursAPI, getTrackingStreamUrl } from './customer-app/api';
import './customer-app/CustomersApp.css';

const translations = {
  mk: {
    navbarTitle: '📋 Закажи термин',
    errorLoadingProviders: 'Грешка при вчитување на даватели на услуги',
    invalidProviderLink: 'Невалиден линк за закажување - ова не е давател на услуги',
    noServicesProvider: 'Овој давател моментално нема достапни услуги',
    errorLoadingProvider: 'Грешка при вчитување на давателот. Проверете го линкот и пробајте повторно.',
    invalidBookingLink: 'Невалиден линк за закажување. Контактирајте го давателот на услуги.',
    errorLoadingServices: 'Грешка при вчитување на услуги',
    errorLoadingTimes: 'Грешка при вчитување на достапни термини',
    fillRequired: 'Ве молиме пополнете ги сите задолжителни полиња',
    bookingSuccess: 'Терминот е успешно закажан!',
    bookingFailedPrefix: 'Неуспешно закажување: ',
    bookingTryAgain: 'Обидете се повторно',
    selectProvider: 'Избери давател на услуги',
    bookingInProgress: 'Се закажува...',
    bookAppointment: 'Закажи термин',
    bookingConfirmed: 'Терминот е успешно закажан!',
    service: 'Услуга',
    date: 'Датум',
    time: 'Време',
    provider: 'Давател',
    duration: 'Времетраење',
    price: 'Цена',
    minutes: 'минути',
    fullName: 'Име и презиме *',
    phoneNumber: 'Телефон *',
    yourInfo: 'Ваши информации',
    confirmationNote: 'Зачувај го линкот за следење за да го провериш статусот.',
    selectDate: 'Избери датум',
    showAvailableTimes: 'Покажи достапни термини',
    selectedDate: 'Избран датум: {{date}}',
    slotAvailable: 'Достапно',
    slotBooked: 'Закажано',
    noSlotsSorry: '🚫 Жалам, {{name}} не е достапен на овој ден.',
    noSlotsHint: 'Избери друг датум или провери работното време.',
    back: '← Назад',
    trackLoading: 'Се вчитува следењето...',
trackUnavailable: 'Следењето моментално не е достапно.',
trackingLinkCopied: 'Линкот е копиран.',
    workingHours: '📅 Работно време',
    showWorkingHours: 'Покажи работно време',
    hideWorkingHours: 'Скриј работно време',
    closed: 'Затворено',
    loadingServices: 'Се вчитуваат услуги...',
    noServices: 'Нема достапни услуги.',
    bookingWith: 'Закажување со {{name}}',
    selectService: 'Избери услуга',
    availableTimesForDate: 'Достапни термини за {{date}}',
    copyTrackingLink: 'Копирај линк за следење',
    openTrackingPage: 'Отвори следење',
    trackTitle: 'Следење на термин',
    trackLive: 'Во живо',
    trackPending: 'На чекање',
    trackStatus: 'Статус',
    trackUpdated: 'Последно ажурирање',
    trackRescheduled: 'Презакажано',
    trackRescheduledTo: 'Презакажано за',
    trackTimeline: 'Историја на промени',
    trackBookedEvent: 'Терминот е закажан',
    trackBack: '← Ново закажување',
    trackStatusUpdated: 'Статусот е ажуриран',
    days: ['Пон', 'Вто', 'Сре', 'Чет', 'Пет', 'Саб', 'Нед'],
    namePlaceholder: 'Име Презиме',
    phonePlaceholder: '07X XXX XXX',
    serviceProvider: 'Давател на услуги',
    trackConfirmed: 'Потврдено',
    trackDeclined: 'Одбиено',
    trackCancelled: 'Откажано',
    trackingSection: 'Секција за следење',
    trackingHint: 'Користете го линкот за следење за да го проверите статусот на вашиот термин.',
  },
  en: {
    // ...existing en translations...
  }
};

const translate = (language, key, params = {}) => {
  const dict = translations[language] || translations.en;
  const fallback = translations.en[key];
  const value = dict[key] ?? fallback ?? key;

  if (typeof value !== 'string') return value;

  return Object.entries(params).reduce(
    (text, [paramKey, paramValue]) => text.replace(`{{${paramKey}}}`, String(paramValue ?? '')),
    value
  );
};

// Helper function to get local date string without timezone conversion
const getLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const filterFutureSlotsForDate = (slots, dateString) => {
  if (!Array.isArray(slots)) return [];

  const now = new Date();
  const todayString = getLocalDateString(now);

  if (dateString !== todayString) {
    return slots;
  }

  return slots.filter((slot) => {
    if (!slot?.available) return false;

    

    const [hours, minutes] = String(slot.time || '00:00').split(':').map(Number);
    const slotDate = new Date(now);
    slotDate.setHours(hours || 0, minutes || 0, 0, 0);
    return slotDate > now;
  });
};

const parseAppointmentLocal = (value) => {
  if (!value || typeof value !== 'string') return null;

  const parts = value.split(/[-T:.\s]/).filter(Boolean);
  if (parts.length < 5) return null;

  const [year, month, day, hour, minute, second = '0'] = parts.map(Number);
  if ([year, month, day, hour, minute, second].some(Number.isNaN)) return null;

  return new Date(year, month - 1, day, hour, minute, second);
};

const formatSystemDateTime = (value) => {
  if (!value) return '-';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);

  const formatter = new Intl.DateTimeFormat('mk-MK', {
    timeZone: 'Europe/Skopje',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  return formatter.format(parsed);
};

const formatDateTime = (value) => {
  const date = parseAppointmentLocal(value);
  if (!date) return '-';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

function App() {
  // Store booking confirmation date/time for tracking info box
  const [confirmedDate, setConfirmedDate] = useState('');
  const [confirmedTime, setConfirmedTime] = useState('');
  const [language] = useState('mk');
  const [bookingCooldown, setBookingCooldown] = useState(false);
  const [step, setStep] = useState(1); // 1: Select User, 2: Select Service, 3: Select Date, 4: Select Time, 5: Guest Info, 6: Confirm
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');
  const [guestInfo, setGuestInfo] = useState({
    name: '',
    phone: '',
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [trackingMode, setTrackingMode] = useState(false);
  const [trackingUrl, setTrackingUrl] = useState('');
  const [trackingBackUrl, setTrackingBackUrl] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState('');
  const [trackingConnected, setTrackingConnected] = useState(false);
  const [userId, setUserId] = useState(null);
  const [userWorkHours, setUserWorkHours] = useState({});
  const [isPhoneView, setIsPhoneView] = useState(false);
  const [showPhoneWorkHours, setShowPhoneWorkHours] = useState(false);
  const trackingEventSourceRef = useRef(null);
  const daysOfWeek = translate(language, 'days');
  const dayOrder = [1, 2, 3, 4, 5, 6, 0]; // Mon=1, Tue=2, ..., Sun=0

  // On booking confirmation step, save date/time to localStorage and state
  useEffect(() => {
    if (!trackingMode && step === 6 && selectedDate && selectedTime) {
      const date = formatDateTime(`${selectedDate}T${selectedTime}`)?.split(' ')[0];
      const time = formatDateTime(`${selectedDate}T${selectedTime}`)?.split(' ')[1];
      setConfirmedDate(date);
      setConfirmedTime(time);
      if (typeof window !== 'undefined') {
        localStorage.setItem('confirmedDate', date);
        localStorage.setItem('confirmedTime', time);
      }
    }
  }, [step, trackingMode, selectedDate, selectedTime]);

  // On tracking info box, load date/time from localStorage if available
  useEffect(() => {
    if (trackingMode) {
      if (typeof window !== 'undefined') {
        const date = localStorage.getItem('confirmedDate');
        const time = localStorage.getItem('confirmedTime');
        if (date && time) {
          setConfirmedDate(date);
          setConfirmedTime(time);
        }
      }
    }
  }, [trackingMode]);

  // ...existing code...

  const isPhoneViewport = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 480px)').matches;
  };

  const buildProviderBookingUrl = (providerTokenValue, providerUserIdValue) => {
    if (typeof window === 'undefined') {
      return '';
    }

    const baseUrl = `${window.location.origin}${window.location.pathname}`;
    const params = new URLSearchParams();

    if (providerTokenValue) {
      params.set('provider', providerTokenValue);
    } else if (providerUserIdValue) {
      params.set('userId', String(providerUserIdValue));
    }

    const query = params.toString();
    return query ? `${baseUrl}?${query}` : baseUrl;
  };

  const buildTrackingUrlWithContext = (token, rawTrackingUrl, providerTokenValue, providerUserIdValue) => {
    if (!token || typeof window === 'undefined') {
      return rawTrackingUrl || '';
    }

    // Always use /customer route for tracking links
    const baseUrl = `${window.location.origin}/customer`;
    const params = new URLSearchParams();
    params.set('track', token);
    if (providerTokenValue) {
      params.set('provider', providerTokenValue);
      params.delete('userId');
    } else if (providerUserIdValue) {
      params.set('userId', String(providerUserIdValue));
    }
    const query = params.toString();
    return `${baseUrl}?${query}`;
  };

  // One-time initialization from URL query params.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Check if provider token or userId is in URL
    const params = new URLSearchParams(window.location.search);
    const trackTokenFromUrl = params.get('track');
    const providerToken = params.get('provider');
    const userIdFromUrl = params.get('userId');

    if (trackTokenFromUrl) {
      setTrackingMode(true);
      const initialTrackingUrl = buildTrackingUrlWithContext(
        trackTokenFromUrl,
        `${window.location.origin}${window.location.pathname}${window.location.search}`,
        providerToken,
        userIdFromUrl
      );
      setTrackingUrl(initialTrackingUrl);
      setTrackingBackUrl(buildProviderBookingUrl(providerToken, userIdFromUrl));
      fetchTrackingDetails(trackTokenFromUrl);
      connectTrackingStream(trackTokenFromUrl);
    } else {
      if (providerToken) {
        fetchUserDetailsByToken(providerToken);
      } else if (userIdFromUrl) {
        setUserId(parseInt(userIdFromUrl, 10));
        fetchUserDetails(parseInt(userIdFromUrl, 10));
      } else {
        fetchUsers();
      }
    }

    return () => {
      if (trackingEventSourceRef.current) {
        trackingEventSourceRef.current.close();
        trackingEventSourceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateViewportFlags = () => {
      const phoneView = isPhoneViewport();
      setIsPhoneView(phoneView);
      if (!phoneView) {
        setShowPhoneWorkHours(false);
      }
    };

    updateViewportFlags();
    window.addEventListener('resize', updateViewportFlags);
    return () => window.removeEventListener('resize', updateViewportFlags);
  }, []);

  const statusToLabel = (status) => {
    if (status === 'rescheduled') return translate(language, 'trackRescheduled');
    if (status === 'confirmed') return translate(language, 'trackConfirmed');
    if (status === 'declined') return translate(language, 'trackDeclined');
    if (status === 'cancelled') return translate(language, 'trackCancelled');
    return translate(language, 'trackPending');
  };

  const eventToLabel = (event) => {
    if (event.event_type === 'rescheduled') {
      const newDate = event.payload?.new_appointment_date;
      if (newDate) {
        return `${translate(language, 'trackRescheduledTo')}: ${formatDateTime(newDate)}`;
      }
      return translate(language, 'trackRescheduled');
    }
    if (event.event_type === 'booked') return translate(language, 'trackBookedEvent');

    if (event.event_type === 'status_updated') {
      const nextStatus = event.payload?.new_status;
      if (nextStatus) {
        return `${translate(language, 'trackStatusUpdated')}: ${statusToLabel(nextStatus)}`;
      }
      return translate(language, 'trackStatusUpdated');
    }

    return event.event_type;
  };

  const fetchTrackingDetails = async (token) => {
    try {
      setTrackingLoading(true);
      setTrackingError('');
      const response = await appointmentAPI.getTrackingDetails(token);
      setTrackingData(response.data);

      if (!trackingBackUrl) {
        const providerToken = response.data?.appointment?.provider_booking_token || '';
        const providerUserId = response.data?.appointment?.provider_id || '';
        const resolvedBackUrl = buildProviderBookingUrl(providerToken, providerUserId);
        if (resolvedBackUrl) {
          setTrackingBackUrl(resolvedBackUrl);
        }
      }
    } catch (err) {
      console.error('Tracking fetch error:', err);
      setTrackingError(translate(language, 'trackUnavailable'));
    } finally {
      setTrackingLoading(false);
    }
  };

  const connectTrackingStream = (token) => {
    if (!token) {
      return;
    }

    if (trackingEventSourceRef.current) {
      trackingEventSourceRef.current.close();
      trackingEventSourceRef.current = null;
    }

    const streamUrl = getTrackingStreamUrl(token);
    const source = new EventSource(streamUrl);
    trackingEventSourceRef.current = source;

    source.onopen = () => {
      setTrackingConnected(true);
    };

    source.addEventListener('tracking_update', (event) => {
      try {
        const parsed = JSON.parse(event.data);
        if (parsed?.data) {
          setTrackingData(parsed.data);
          setTrackingConnected(true);
        }
      } catch (parseError) {
        console.error('Invalid tracking event payload:', parseError);
      }
    });

    source.onerror = () => {
      setTrackingConnected(false);
    };
  };

  const copyTrackingLink = async () => {
    if (!trackingUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(trackingUrl);
      setMessage(translate(language, 'trackingLinkCopied'));
    } catch (error) {
      console.error('Failed to copy tracking link:', error);
    }
  };

  const openTrackingPage = () => {
    if (!trackingUrl) return;
    window.open(trackingUrl, '_blank');
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAllUsers();
      setUsers(response.data.filter(u => u.role === 'user'));
    } catch (err) {
      console.error('Error fetching users:', err);
      setMessage(translate(language, 'errorLoadingProviders'));
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (id) => {
    try {
      setLoading(true);
      const response = await userAPI.getUserById(id);
      const user = response.data;

      if (user.role !== 'user') {
        setMessage(translate(language, 'invalidProviderLink'));
        return;
      }

      setSelectedUser(user);
      setUserId(user.id);

      const servicesResponse = await userAPI.getServicesByUser(user.id);
      setServices(servicesResponse.data);

      // Fetch work hours
      try {
        const workHoursResponse = await workHoursAPI.getWorkHours(user.id);
        const workHoursMap = {};
        workHoursResponse.data.forEach(wh => {
          workHoursMap[wh.day_of_week] = wh;
        });
        setUserWorkHours(workHoursMap);
      } catch (err) {
        console.error('Error fetching work hours:', err);
      }

      if (servicesResponse.data.length === 0) {
        setMessage(translate(language, 'noServicesProvider'));
      }

      setStep(2);
    } catch (err) {
      console.error('Error fetching user:', err);
      setMessage(translate(language, 'errorLoadingProvider'));
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetailsByToken = async (token) => {
    try {
      setLoading(true);
      const response = await userAPI.getUserByBookingToken(token);
      const user = response.data;

      setSelectedUser(user);
      setUserId(user.id);

      const servicesResponse = await userAPI.getServicesByUser(user.id);
      setServices(servicesResponse.data);

      // Fetch work hours
      try {
        const workHoursResponse = await workHoursAPI.getWorkHours(user.id);
        const workHoursMap = {};
        workHoursResponse.data.forEach(wh => {
          workHoursMap[wh.day_of_week] = wh;
        });
        setUserWorkHours(workHoursMap);
      } catch (err) {
        console.error('Error fetching work hours:', err);
      }

      if (servicesResponse.data.length === 0) {
        setMessage(translate(language, 'noServicesProvider'));
      }

      setStep(2);
    } catch (err) {
      console.error('Error fetching user by token:', err);
      setMessage(translate(language, 'invalidBookingLink'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    try {
      const response = await userAPI.getServicesByUser(user.id);
      setServices(response.data);
      
      // Fetch work hours for the selected user
      try {
        const workHoursResponse = await workHoursAPI.getWorkHours(user.id);
        const workHoursMap = {};
        workHoursResponse.data.forEach(wh => {
          workHoursMap[wh.day_of_week] = wh;
        });
        setUserWorkHours(workHoursMap);
      } catch (err) {
        console.error('Error fetching work hours:', err);
      }
      
      setStep(2);
    } catch (err) {
      console.error('Error fetching services:', err);
      setMessage(translate(language, 'errorLoadingServices'));
    }
  };

  const handleSelectService = (service) => {
  setSelectedService(service);
  setSelectedDate('');
  setSelectedTime('');
  setAvailableSlots([]);
  setStep(3);
};

  const loadAvailableSlotsForDate = async (date) => {
    try {
      const response = await appointmentAPI.getAvailableSlots(selectedUser.id, date, selectedService.duration);
      // Only show slots at 00 or 30 minutes (30-minute intervals)
      const filteredSlots = filterFutureSlotsForDate(response.data, date).filter(slot => {
        if (!slot?.time) return false;
        const minute = slot.time.split(':')[1];
        return minute === '00' || minute === '30';
      });
      setAvailableSlots(filteredSlots);
      setStep(4);
    } catch (err) {
      console.error('Error fetching slots:', err);
      setMessage(translate(language, 'errorLoadingTimes'));
    }
  };

  // ...existing code...

  const handleContinueAfterDateSelect = async () => {
    if (!selectedDate) {
      return;
    }

    await loadAvailableSlotsForDate(selectedDate);
  };

  const handleSelectTime = (time) => {
    setSelectedTime(time);
    setStep(5);
  };

  const handleConfirmBooking = async (e) => {
    e.preventDefault();
    if (!guestInfo.name || !guestInfo.phone) {
      setMessage(translate(language, 'fillRequired'));
      return;
    }
    // Validate selectedDate and selectedTime
    if (!selectedDate || !/^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
      setMessage(translate(language, 'fillRequired') + ' (' + translate(language, 'date') + ')');
      return;
    }
    if (!selectedTime || !/^\d{2}:\d{2}$/.test(selectedTime)) {
      setMessage(translate(language, 'fillRequired') + ' (' + translate(language, 'time') + ')');
      return;
    }

    if (bookingCooldown) {
      setMessage('Please wait before booking again.');
      return;
    }
    setBookingCooldown(true);
    setTimeout(() => setBookingCooldown(false), 5000);
    try {
      setLoading(true);
      // Construct local time string (no Z)
      const appointmentDate = `${selectedDate}T${selectedTime}:00.000`;

      const bookingResponse = await appointmentAPI.bookAppointment({
        user_id: userId || selectedUser.id,
        service_id: selectedService.id,
        appointment_date: appointmentDate,
        guest_name: guestInfo.name,
        guest_phone: guestInfo.phone,
      });

      const responseData = bookingResponse.data || {};
      const token = responseData.tracking_token || responseData.appointment?.tracking_token || '';
      const providedUrl = responseData.tracking_url || '';

      const providerToken = selectedUser?.booking_token || new URLSearchParams(window.location.search).get('provider') || '';
      const providerUserId = selectedUser?.id || userId || '';
      const resolvedTrackingUrl = buildTrackingUrlWithContext(token, providedUrl, providerToken, providerUserId);

      setTrackingUrl(resolvedTrackingUrl);
      setTrackingBackUrl(buildProviderBookingUrl(providerToken, providerUserId));

      setMessage(translate(language, 'bookingSuccess'));
      setStep(6);
    } catch (err) {
      setMessage(translate(language, 'bookingFailedPrefix') + (err.response?.data?.error || translate(language, 'bookingTryAgain')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app">
      <nav className="navbar">
        <h1>{translate(language, 'navbarTitle')}</h1>
        {trackingMode
          ? <p className="user-name">{translate(language, 'trackTitle')}</p>
          : selectedUser && <p className="user-name">
    {selectedUser.display_name || selectedUser.name}
    {selectedUser.shop_name ? ` - ${selectedUser.shop_name}` : ''}
  </p>}
      </nav>

      <div className="booking-container">
        {message && step !== 6 && (
  <div className="message error">
    {message}
  </div>
)}

        {trackingMode && (
  <div className="tracking-card">
    <div className="tracking-header">
      <h2 className="tracking-title">{translate(language, 'trackTitle')}</h2>
      <p className="tracking-subtitle">
        Подолу можете да ја видите моменталната состојба и историјата на резервацијата.
      </p>
    </div>

    <div className="tracking-status-wrap">
      <div
        className={`tracking-status-badge ${
          (trackingData?.appointment?.tracking_status || trackingData?.appointment?.status || '').toLowerCase()
        }`}
      >
        {trackingData
          ? statusToLabel(trackingData.appointment.tracking_status || trackingData.appointment.status)
          : ''}
      </div>
    </div>

    {trackingLoading && <p className="loading">{translate(language, 'trackLoading')}</p>}

    {!trackingLoading && trackingError && (
      <div className="no-data">{trackingError}</div>
    )}

    {!trackingLoading && !trackingError && trackingData && (
      <>
        <div className="tracking-summary">
          <div className="tracking-summary-item">
            <span className="tracking-summary-label">{translate(language, 'service')}</span>
            <span className="tracking-summary-value">{trackingData.appointment.service_name}</span>
          </div>

          <div className="tracking-summary-item">
            <span className="tracking-summary-label">{translate(language, 'date')}</span>
            <span className="tracking-summary-value">
              {confirmedDate || formatDateTime(trackingData.appointment.appointment_date)?.split(' ')[0]}
            </span>
          </div>

          <div className="tracking-summary-item">
            <span className="tracking-summary-label">{translate(language, 'time')}</span>
            <span className="tracking-summary-value">
              {confirmedTime || formatDateTime(trackingData.appointment.appointment_date)?.split(' ')[1]}
            </span>
          </div>

          <div className="tracking-summary-item">
            <span className="tracking-summary-label">{translate(language, 'provider')}</span>
            <span className="tracking-summary-value">{trackingData.appointment.provider_name}</span>
          </div>
        </div>

        <p className="tracking-update-time">
          {translate(language, 'trackUpdated')}: {
            trackingData.events?.[0]?.created_at
              ? formatSystemDateTime(trackingData.events[0].created_at)
              : formatDateTime(trackingData.appointment.appointment_date)
          }
        </p>

        {trackingData.appointment.latest_rescheduled_to &&
          (trackingData.appointment.tracking_status === 'rescheduled' ||
            trackingData.appointment.status === 'rescheduled') && (
            <div className="tracking-summary" style={{ marginTop: '14px' }}>
              <div className="tracking-summary-item">
                <span className="tracking-summary-label">{translate(language, 'trackRescheduledTo')}</span>
                <span className="tracking-summary-value">
                  {formatDateTime(trackingData.appointment.latest_rescheduled_to)}
                </span>
              </div>
            </div>
          )}

        <div className="tracking-timeline-card">
          <h3 className="tracking-timeline-title">{translate(language, 'trackTimeline')}</h3>
          <div className="tracking-timeline">
            {(trackingData.events || []).map((event, index) => (
              <div className="tracking-event" key={`${event.created_at}-${index}`}>
                <div className="tracking-event-dot" />
                <div>
                  <p className="tracking-event-title">{eventToLabel(event)}</p>
                  <p className="tracking-event-meta">{formatSystemDateTime(event.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="confirmation-actions" style={{ marginTop: '18px' }}>
          <button
            className="btn-secondary tracking-back"
            onClick={() => {
              window.location.href = trackingBackUrl || window.location.pathname;
            }}
          >
            {translate(language, 'trackBack')}
          </button>
        </div>
      </>
    )}
  </div>
)}

        {!trackingMode && step === 1 && (
          <div className="step">
            <h2>{translate(language, 'selectProvider')}</h2>
            {loading ? (
  <div className="slots-grid">
    {[...Array(8)].map((_, i) => (
      <div key={i} className="slot skeleton skeleton-slot"></div>
    ))}
  </div>
) : users.length === 0 ? (
              <p className="no-data">{translate(language, 'noProviders')}</p>
            ) : (
              <div className="user-grid">
                {users.map((user) => (
                  <div key={user.id} className="user-cube" onClick={() => handleSelectUser(user)}>
                    <div className="user-cube-front">
                      <div className="user-avatar">
                        <span>{user.name.charAt(0).toUpperCase()}</span>
                      </div>
                      <h3 className="user-cube-name">{user.name}</h3>
                      <p className="user-cube-shop">{user.shop_name || translate(language, 'serviceProvider')}</p>
                      <div className="user-cube-stats">
                        <span>📧 {user.email}</span>
                        <span>📞 {user.phone}</span>
                      </div>
                      <button className="btn-select">{translate(language, 'selectProviderBtn')}</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!trackingMode && step === 2 && (
          <div className="step">
            {userId && <p className="info-msg">
    {selectedUser?.display_name || selectedUser?.name}
    {selectedUser?.phone && ` (${selectedUser.phone})`}
  </p>}
            {!userId && <button onClick={() => setStep(1)} className="btn-back">{translate(language, 'back')}</button>}
            
            {/* Display Work Hours */}
            {Object.keys(userWorkHours).length > 0 && (
              <div className="work-hours-display">
                <div className="work-hours-header-row">
                  <h3>{translate(language, 'workingHours')}</h3>
                  {isPhoneView && (
                    <button
                      type="button"
                      className="work-hours-toggle-btn"
                      onClick={() => setShowPhoneWorkHours((prev) => !prev)}
                    >
                      {showPhoneWorkHours
                        ? translate(language, 'hideWorkingHours')
                        : translate(language, 'showWorkingHours')}
                    </button>
                  )}
                </div>

                {(!isPhoneView || showPhoneWorkHours) && (
                  <div className="work-hours-chips">
                    {daysOfWeek.map((day, displayIndex) => {
                      const dayOfWeek = dayOrder[displayIndex];
                      const dayHours = userWorkHours[dayOfWeek];
                      return (
                        <div key={displayIndex} className={`day-chip ${dayHours ? 'working' : 'off'}`}>
                          <span className="day-name">{day}</span>
                          {dayHours ? (
                            <span className="hours">{dayHours.start_time.substring(0, 5)} - {dayHours.end_time.substring(0, 5)}</span>
                          ) : (
                            <span className="hours">{translate(language, 'closed')}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            
            <h2>{translate(language, 'selectService')}</h2>
            
            {loading ? (
  <div className="service-list">
    {[...Array(4)].map((_, i) => (
      <div key={i} className="service-card skeleton skeleton-service"></div>
    ))}
  </div>
) : services.length === 0 ? (

              <p className="no-data">{translate(language, 'noServices')}</p>
            ) : (
              <div className="service-list">
                {services.map((service) => (
                  <div
  key={service.id}
  className={`service-card ${selectedService?.id === service.id ? 'selected' : ''}`}
  onClick={() => handleSelectService(service)}
>
  <h3>{service.name}</h3>

  {service.description && (
    <p className="service-description">{service.description}</p>
  )}

  <div className="service-meta">
    <span className="service-duration">{service.duration} мин</span>
    <span className="service-price">{service.price} ден</span>
  </div>

  {selectedService?.id === service.id && (
    <span className="service-selected-badge">Избрано</span>
  )}
</div>
                ))}
              </div>
            )}
          </div>
        )}

        {!trackingMode && step === 3 && (
          <div className="step">
            <h2>{translate(language, 'selectDate')}</h2>
            <button onClick={() => setStep(2)} className="btn-back">{translate(language, 'back')}</button>
            <div className="date-picker">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => {
  setSelectedDate(e.target.value);
  setSelectedTime('');
}}
                min={getLocalDateString(new Date())}
                aria-label="Select appointment date"
              />
            </div>
            {selectedDate && <p className="selected">{translate(language, 'selectedDate', { date: selectedDate })}</p>}
            {selectedDate && (
              <button
                type="button"
                className="btn-primary date-continue-btn"
                onClick={handleContinueAfterDateSelect}
                disabled={loading}
              >
                {translate(language, 'showAvailableTimes')}
              </button>
            )}
          </div>
        )}

        {!trackingMode && step === 4 && (
          <div className="step">
            <h2>{translate(language, 'availableTimesForDate', { date: selectedDate })}</h2>
            <button onClick={() => setStep(3)} className="btn-back">{translate(language, 'back')}</button>
            {availableSlots.length === 0 ? (
              <div className="no-slots-message">
                <p>
  {translate(language, 'noSlotsSorry', {
    name: selectedUser?.display_name || selectedUser?.name
  })}
</p>
                <p className="hint">{translate(language, 'noSlotsHint')}</p>
              </div>
            ) : (
              <div className="slots-grid">
                {availableSlots.map((slot, idx) => (
  <button
    key={idx}
    type="button"
    className={`time-slot 
      ${slot.available ? '' : 'unavailable'} 
      ${selectedTime === slot.time ? 'selected' : ''}
    `}
    onClick={() => slot.available && handleSelectTime(slot.time)}
    disabled={!slot.available}
    title={slot.available ? translate(language, 'slotAvailable') : translate(language, 'slotBooked')}
  >
    {slot.time}
  </button>
))}
              </div>
            )}
          </div>
        )}

        {!trackingMode && step === 5 && (
  <div className="step">
    <h2>{translate(language, 'yourInfo')}</h2>

    <button onClick={() => setStep(4)} className="btn-back">
      {translate(language, 'back')}
    </button>

    {/* 🔥 BOOKING SUMMARY */}
    <div className="booking-summary-grid">
  {selectedService?.name && (
    <div className="booking-summary-item">
      <span className="booking-summary-label">Услуга</span>
      <span className="booking-summary-value">{selectedService.name}</span>
    </div>
  )}

  {selectedDate && (
    <div className="booking-summary-item">
      <span className="booking-summary-label">Датум</span>
      <span className="booking-summary-value">{selectedDate}</span>
    </div>
  )}

  {selectedTime && (
    <div className="booking-summary-item">
      <span className="booking-summary-label">Термин</span>
      <span className="booking-summary-value">{selectedTime}</span>
    </div>
  )}

</div>

    {/* 🔽 FORM (НЕ ГО МЕНУВАМЕ) */}
    <form className="guest-form" onSubmit={handleConfirmBooking}>
      <div className="form-group">
        <label>{translate(language, 'fullName')}</label>
        <input
          type="text"
          value={guestInfo.name}
          onChange={(e) =>
            setGuestInfo({ ...guestInfo, name: e.target.value })
          }
          placeholder={translate(language, 'namePlaceholder')}
          required
        />
      </div>

      <div className="form-group">
        <label>{translate(language, 'phoneNumber')}</label>
        <input
          type="tel"
          value={guestInfo.phone}
          onChange={(e) =>
            setGuestInfo({ ...guestInfo, phone: e.target.value })
          }
          placeholder={translate(language, 'phonePlaceholder')}
          required
        />
      </div>

      <button
        type="submit"
        className="btn-primary"
        disabled={loading || bookingCooldown}
      >
        {loading
          ? translate(language, 'bookingInProgress')
          : bookingCooldown
          ? 'Please wait...'
          : translate(language, 'bookAppointment')}
      </button>
    </form>
  </div>
)}

        {!trackingMode && step === 6 && (
  <div className="confirmation-card">
    <div className="confirmation-hero">
      <div className="confirmation-icon">✓</div>
      <h2 className="confirmation-title">Вашиот термин е резервиран</h2>
      <p className="confirmation-subtitle">
        Вашата резервација е успешно примена. Подолу можете да ги проверите деталите и линкот за следење.
      </p>
    </div>

    <div className="confirmation-summary">
      {selectedService?.name && (
        <div className="confirmation-summary-item">
          <span className="confirmation-summary-label">Услуга</span>
          <span className="confirmation-summary-value">{selectedService.name}</span>
        </div>
      )}

      {selectedDate && (
        <div className="confirmation-summary-item">
          <span className="confirmation-summary-label">Датум</span>
          <span className="confirmation-summary-value">
            {formatDateTime(`${selectedDate}T${selectedTime}`)?.split(' ')[0]}
          </span>
        </div>
      )}

      {selectedTime && (
        <div className="confirmation-summary-item">
          <span className="confirmation-summary-label">Термин</span>
          <span className="confirmation-summary-value">
            {formatDateTime(`${selectedDate}T${selectedTime}`)?.split(' ')[1]}
          </span>
        </div>
      )}

      {guestInfo?.name && (
        <div className="confirmation-summary-item">
          <span className="confirmation-summary-label">Име</span>
          <span className="confirmation-summary-value">{guestInfo.name}</span>
        </div>
      )}
    </div>

    {trackingUrl && (
  <div className="confirmation-tracking-card">
    <h3 className="confirmation-tracking-title">Линк за следење</h3>
    <p className="confirmation-tracking-text">
      Можете да ја следите состојбата на резервацијата преку копчето подолу.
    </p>
  </div>
)}

    <div className="confirmation-actions">
      <button
        type="button"
        className="btn-primary"
        onClick={() => navigator.clipboard.writeText(trackingUrl || '')}
      >
        Копирај линк
      </button>

      <button
        type="button"
        className="btn-secondary"
        onClick={() => trackingUrl && window.open(trackingUrl, '_blank')}
      >
        Отвори следење
      </button>
    </div>
  </div>
)}
      </div>
    </div>
  );
}

export default App;
